/**
 * Labels, defaults and slider config for the financing break-even calculator.
 *
 * English-only by design: the tool lives under /tools, which ships to the
 * English locales only (see EN_ONLY_SLUGS). Currency is the one locale-varying
 * piece and is resolved at render time from the request locale, not hardcoded
 * here.
 */

export const STRINGS = {
  toolTitle: "Your numbers",
  // Inputs
  margin: "Gross margin",
  marginHelp: "Don’t know your margin? Work it out from your prices",
  marginHelpClose: "Hide price and cost",
  price: "Sale price",
  cost: "Unit cost (COGS)",
  pricesNote:
    "Enter your price and cost and the margin fills in. If you leave sale price blank we assume 100.",
  // One amount, not two: the break-even share turns on the fee and the margin
  // alone, so the total order size never moved the result and only invited the
  // question of why it didn't.
  finance: "Stock purchase amount you’ll finance",
  financeNote: "The Black Friday stock you’ll fund with financing.",
  sell: "Expected sell-through",
  sellNote: "The share of the extra stock you expect to sell in the window.",
  fee: "Financing fee",
  feeNote: "Total, over the life of the financing.",
  // Result
  eyebrow: "Break-even sell-through",
  eyebrowFree: "It’s free money",
  eyebrowLoss: "This one doesn’t pay off",
  leadOk:
    "is all of the extra stock you need to sell for the financing to pay for itself.",
  leadFree:
    "At a 0% fee there is nothing to pay back. Every unit of the extra stock you sell is pure upside.",
  leadLoss:
    "You would need to sell more than the entire extra order just to cover the fee.",
  emptyPrompt:
    "Enter a gross margin above zero (or a sale price above your unit cost) to see your break-even.",
  // Runway legend
  legendFee: "Pays the fee",
  legendProfit: "Profit you would bank",
  legendHeadroom: "Headroom left",
  legendNever: "Never fully covered",
  runwayAxis: "Sell-through of the extra stock",
  // Tiles
  tileFinanced: "Amount financed",
  tileFee: "Financing fee",
  tileBreakEven: "Break-even sell-through",
  tileBest: "Best case (full sell-through)",
  // Disclaimer
  disclaimer:
    "The default fee is for illustration only. It is not an offer of financing, a quote, or indicative of any Wayflyer financing terms. Actual fees depend on your application. Estimates only, based on the numbers you enter.",
} as const;

/** Neutral, round placeholder values the user is meant to change. Not derived
 * from any Wayflyer merchant data. A 60% margin with a 5% fee gives a ~3.3%
 * break-even sell-through, comfortably below the 90% a founder would expect. */
export const DEFAULTS = {
  margin: "60",
  price: "100",
  cost: "40",
  finance: "100,000",
  fee: 5,
  sell: 90,
} as const;

export const RANGES = {
  margin: { min: 1, max: 90, step: 1 },
  fee: { min: 0, max: 30, step: 0.1 },
  sell: { min: 5, max: 100, step: 1 },
} as const;

export interface FaqQuestion {
  question: string;
  answer: string;
}

export const FAQ_TITLE = "Financing break-even FAQ";

export const FAQ_QUESTIONS: FaqQuestion[] = [
  {
    question: "What is a financing break-even calculator?",
    answer:
      "It shows the exact share of a financed stock order you need to sell for the financing fee to pay for itself, given your margin. Enter your gross margin, how much you're financing and the fee, and it works out your break-even sell-through, plus what full sell-through would mean for profit.",
  },
  {
    question: "Is it worth financing extra stock for Black Friday?",
    answer:
      "It depends on whether the margin from the extra sales it unlocks beats the fee, which is exactly what this tool checks. Financing extra inventory can help you meet demand you'd otherwise miss during the sale, but it only pays off if enough of that stock actually sells.",
  },
  {
    question: "What is break-even sell-through, and how is it calculated?",
    answer:
      "Break-even sell-through is the share of the financed stock you need to sell, at your margin, for the profit on those sales to cover the financing fee. Below that share, the financing costs you more than it earns; above it, every extra unit sold is profit on top.",
  },
  {
    question: "What's the biggest risk of financing inventory?",
    answer:
      "The main risk is the stock not selling as expected: if sell-through falls short of your break-even, the financing hasn't paid for itself and you're carrying both the fee and the unsold inventory. That's why this tool asks for your expected sell-through directly, rather than assuming everything you finance will sell.",
  },
  {
    question: "What does it mean if the fee is 0%?",
    answer:
      "At a 0% fee there's nothing to pay back, so every unit of the extra stock you sell is pure upside with no break-even to clear. The default fee in this tool is for illustration only and isn't a quote or an offer of financing.",
  },
  {
    question: "What if the calculator says the financing doesn't pay off?",
    answer:
      "That means the break-even sell-through is above 100%, so you'd need to sell more than the entire extra order just to cover the fee at your current margin. Revisit the margin, the amount financed or the fee before committing, since as entered the numbers don't work in your favour.",
  },
  {
    question: "Where can I get help with the rest of my Black Friday numbers?",
    answer:
      "Check the maths on the discounts you're financing stock for with the [discount break-even calculator](/tools/discount-break-even), work out how much stock to order in the first place with the [order quantity calculator](/tools/order-quantity-calculator), or browse the [full Black Friday toolkit](/tools).",
  },
];
